import { ACUANT_SDK_ERROR_PAGES } from 'utils/constants';

export const GOVT_ID_SESSION_TIMEOUT_MINUTES = 10;

export const IMAGE_CROPPING_MODE = { NONE: 0, AUTOMATIC: 1, ALWAYS: 3 };

export const ID_STATUS = {
    ACCEPT: 'ACCEPT',
    REJECT: 'REJECT',
    SOFT_FAIL: 'SOFT_FAIL',
    ATTENTION: 'ATTENTION',
    INCONCLUSIVE: 'INCONCLUSIVE',
};

export const SCREEN_TO_RENDER = {
    RESULT: '/govtid/results/default',
    DEFAULT: '/govtid/error/default',
    ID_EXPIRED: '/govtid/error/idexpired',
    COUNTRY_MISMATCH: '/govtid/error/country_mismatch',
    INVALID_ID: '/govtid/error/invalid_id',
    INVALID_DATE_OF_BIRTH: '/govtid/error/invalid_date_of_birth',
};

export const ERROR_SCREEN_PATHS = {
    DEFAULT: '/govtid/error/default',
    ID_SIDES_FAILED: '/govtid/error/id_sides_failed',
    TAMPER_RESULT_FAILED: '/govtid/error/tamper-result/failed',
    COUNTRY_MISMATCH: '/govtid/error/country_mismatch',
    ID_FAILED: '/verification/idFailed',
    INVALID_DOCUMENT_TYPE: '/govtid/error/invalid_document_type',
    ID_TIMEOUT: '/govtid/idTimeout',
};

// `detail.error.code` values dispatched by <live-document-camera>; the SDK exports no constants.
export const SDK_ERROR_CODES = {
    START_FAIL: 'start-fail',
    RUNTIME_ERROR: 'runtime-error',
    SEQUENCE_BREAK: 'sequence-break',
    AUTO_CAPTURE_TIMEOUT: 'auto-capture-timeout',
};

export const SDK_ERROR_PAGES = {
    [SDK_ERROR_CODES.START_FAIL]: ACUANT_SDK_ERROR_PAGES.START_FAIL_CODE,
    [SDK_ERROR_CODES.RUNTIME_ERROR]: ACUANT_SDK_ERROR_PAGES.RUNTIME_ERROR_CODE,
};

// Auto-capture cannot finish in this session: the SDK's guidance for both is to switch to
// manual (tap-to-capture), so the retry relaunches the camera with auto-capture off.
export const MANUAL_CAPTURE_FALLBACK_CODES = new Set([
    SDK_ERROR_CODES.SEQUENCE_BREAK,
    SDK_ERROR_CODES.AUTO_CAPTURE_TIMEOUT,
]);

export const ERROR_DESCRIPTIONS = {
    DEFAULT: 'Unable to read ID.',
    FRONT_NOT_READABLE: "We couldn't read the front of your ID.",
    BACK_NOT_READABLE: "We couldn't read the back of your ID.",
    DATA_MISMATCH: "These two photos don't match.",
    LIVENESS_FAILED: 'Photograph the physical card.',
    AUTHENTICITY_FAILED: "Card details didn't come through.",
    INVALID_NAME: "We couldn't read your name.",
    LOW_RESOLUTION: 'The photo is too blurry to read.',
    BACK_SIDE_REQUIRED: 'We need a clearer photo of the back of your ID.',
};

export const SELFIE_ERROR_DESCRIPTIONS = {
    DEFAULT:
        "We couldn't complete your selfie check. Please make sure you have a stable internet connection and keep only your face in the frame before trying again.",
    TIMEOUT:
        'Your selfie session has timed out. Please try again and position your face within the frame when prompted.',
    CAMERA_FAILED: "We couldn't access your camera. This may happen if:",
};

export const ERROR_FALLBACK =
    'If this keeps happening, use a different government-issued ID or contact support.';

export const CAPTURE_QUALITY_GUIDANCE_COPY = {
    TITLE: "Let's try a different spot",
    BODY: "We couldn't get a clear image of your ID. Please move to a different spot, then try again. It only takes a moment.",
    TIPS: {
        LIGHT: 'Face a window or a bright lamp',
        GLARE: 'Avoid direct overhead light and glare',
        SURFACE: 'Lay your ID on a plain, dark surface',
    },
    CTA: 'Try again',
};

export const AUTHENTICATION_TYPES = [0, 1, 2, 3, 4, 5];

export const DOCUMENT_TYPES = {
    DRIVERS_LICENSE: 'DRIVERS LICENSE',
    DRIVER_LICENSE: 'DRIVER LICENSE',
    IDENTIFICATION_CARD: 'IDENTIFICATION CARD',
    RESIDENCE_DOCUMENT: 'RESIDENCE DOCUMENT',
    MILITARY: 'MILITARY',
    PASSPORT: 'PASSPORT',
    VISA: 'VISA',
    VOTER_IDENTIFICATION: 'VOTER IDENTIFICATION',
    PERMIT: 'PERMIT',
};

/**
 * What to list when a workflow names no document types of its own — telling someone their document
 * was rejected and then showing them nothing to use instead is worse than naming the full set.
 *
 * Written out rather than derived from `DOCUMENT_TYPES` above: that map carries both spellings of a
 * driving licence so either can be matched against a result, and listing the same document twice is
 * exactly what a reader would read as a mistake. Ordered by how often each is actually presented.
 */
export const ALL_DOCUMENT_TYPES = [
    DOCUMENT_TYPES.DRIVERS_LICENSE,
    DOCUMENT_TYPES.IDENTIFICATION_CARD,
    DOCUMENT_TYPES.PASSPORT,
    DOCUMENT_TYPES.RESIDENCE_DOCUMENT,
    DOCUMENT_TYPES.VISA,
    DOCUMENT_TYPES.MILITARY,
    DOCUMENT_TYPES.VOTER_IDENTIFICATION,
    DOCUMENT_TYPES.PERMIT,
];

// Medical-card result keys that are images or backend noise — never shown as data rows.
export const MEDICAL_CARD_SKIP_KEYS = new Set([
    'FrontImage',
    'BackImage',
    'RawText',
    'TransactionTimestamp',
    'MemberName',
]);

// ID verification Result component objects 👇

export const govtIdObject = [
    { name: 'scannedUser', display: 'Photo ID' },
    { name: 'passport', display: 'Passport Data' },
];

export const ORDERED_FIELDS = [
    // Verification metadata
    { keys: ['method'], label: 'Method' },
    { keys: ['attempts'], label: 'Attempts' },
    { keys: ['result'], label: 'Result' },
    { keys: ['authentication_result'], label: 'Authentication Result' },
    {
        keys: ['confidence_rating', 'DocumentVerificationConfidenceRating'],
        label: 'Confidence Rating',
    },
    // Personal information
    { keys: ['firstname', 'first_name'], label: 'First Name' },
    { keys: ['middlename', 'middle_name'], label: 'Middle Name' },
    { keys: ['lastname', 'last_name'], label: 'Last Name' },
    { keys: ['dob', 'DateOfBirth', 'DateOfBirth4'], label: 'Date of Birth', noCase: true },
    { keys: ['gender'], label: 'Gender' },
    { keys: ['nationality'], label: 'Nationality' },
    // Address
    { keys: ['address', 'streetName'], label: 'Address' },
    { keys: ['city'], label: 'City' },
    { keys: ['state'], label: 'State' },
    // 'zip' is the key used by the Acuant API; zip_code/zipCode are aliases for other sources
    { keys: ['zip_code', 'zipCode', 'zip'], label: 'Zip Code', noCase: true },
    // Document identifiers
    // 'documentType' is the passport API key for the same concept as 'idType' on driver's license
    { keys: ['id_type', 'idType', 'documentType', 'document_type'], label: 'ID Type' },
    // Dynamic label based on matched key (see DYNAMIC_LABEL_MAP):
    //   passportNumber → 'Passport Number', license → 'Driver License Number', others → 'ID Number'
    // NOTE: mrz is the full Machine Readable Zone string and is intentionally excluded here;
    //       it is shown separately at the end of the list via its own entry below.
    {
        keys: ['passportNumber', 'passport_number', 'idNumber', 'id_number', 'license', 'id'],
        label: 'ID Number',
        noCase: true,
        isDynamicLabel: true,
    },
    { keys: ['id_issued_date', 'IssueDate', 'IssueDate4'], label: 'ID Issue Date', noCase: true },
    {
        keys: ['id_expiration_date', 'ExpirationDate', 'ExpirationDate4'],
        label: 'ID Expiration Date',
        noCase: true,
    },
    // Liveness (nested under object.liveness or object.liveness.LivenessResult)
    { keys: ['LivenessAssessment'], nestedParent: 'liveness', label: 'Liveness Assessment' },
    { keys: ['Score', 'score'], nestedParent: 'liveness', label: 'Liveness Score' },
    { keys: ['LivenessError'], nestedParent: 'liveness', label: 'Liveness Error', noCase: true },
    // Face match
    {
        keys: ['face_match_score', 'faceMatch', 'FaceMatchScore'],
        label: 'Face Match Score',
        noCase: true,
    },
    // Full Machine Readable Zone — passport only, always shown last in the ordered section
    { keys: ['mrz'], label: 'Machine Readable Zone(MRZ)', noCase: true },
];

export const DYNAMIC_LABEL_MAP = {
    passportNumber: 'Passport Number',
    passport_number: 'Passport Number',
    license: 'Driver License Number',
};

// Fields from the API response that should never be displayed (images, internal codes, etc.)
export const REMOVED_KEYS = new Set([
    'web_response_code',
    'id_front',
    'id_back',
    'face_image',
    'signature_image',
    'selfie_image',
    'watermarkedImages',
]);

// ID verification Result component objects 👆
